import {
  ActivityReply,
  FileAttachment,
  ImageAttachment,
} from "../../../../@types/activities.ts";
import { useState } from "react";
import MemberlinkWrapper from "../../../shared/MemberlinkWrapper.tsx";
import ReplyComposer from "../../replyComposer";
import { groupBy, isEmpty, omit } from "lodash";
import TruncateableContent from "../TruncateableContent.tsx";
import ImageAttachments from "../object/attachments/ImageAttachments.tsx";
import FileAttachments from "../object/attachments/FileAttachments.tsx";
import ReplyHeader from "./ReplyHeader.tsx";
import ReplyInteractions from "./ReplyInteractions.tsx";
import ReplyQuote from "./ReplyQuote.tsx";
import { useUpdateActivityReplyInCache } from "../../../../hooks/useUpdateActivityReplyInCache.ts";
import TranslationIndicator from "@/components/shared/TranslationIndicator.tsx";
import classNames from "classnames";

export default function Reply({
  activityId,
  quoteReply,
  processType = "replies",
  ...reply
}: ActivityReply & {
  activityId: string;
  quoteReply: () => void;
  processType: "replies" | "answers";
}) {
  const {
    id,
    content,
    published,
    content_updated_at,
    content_updated_by,
    can,
    author,
    attachments,
  } = reply;
  const { updateReply } = useUpdateActivityReplyInCache();
  const [showComposer, setShowComposer] = useState(false);

  const [translatedContent, setTranslatedContent] = useState<{
    language?: string;
    content?: string;
    quoted_content?: string;
  }>({
    language: undefined,
    content: undefined,
    quoted_content: undefined,
  });

  const groupedAttachments = groupBy(attachments, "type");

  const fileAttachments = groupedAttachments.file;
  const imageAttachments = groupedAttachments.image;

  const isReply = processType === "replies";

  return (
    <div
      id={`reply-${id}`}
      className={classNames("flex gap-2", {
        "px-5": window.isApp && isReply,
      })}
    >
      <div className="member-image shrink-0">
        <MemberlinkWrapper url={author.url}>
          <img
            className="h-10 thumb-small w-10 print:h-8 print:w-8"
            src={author.images["40x40@2"]}
            alt={author.displayName}
          />
        </MemberlinkWrapper>
      </div>
      {/* may shrink below its content, so a code block or a long link
          scrolls or wraps inside the bubble instead of widening it past the
          column and pushing the context menu out of sight (tixxt/core#712) */}
      <div className="grow min-w-0">
        <div
          className={classNames("grow", {
            // Question answers use the theme's answer colors via
            // question.css instead of the fixed gray reply bubble (#102).
            "answer-container": !isReply,
            "reply-bubble bg-gray-100 px-3 py-2 rounded-lg rounded-tl-none":
              isReply && !window.isApp,
          })}
        >
          {showComposer ? (
            <ReplyComposer
              type={processType}
              activityId={activityId}
              reply={reply}
              cancelCallback={() => setShowComposer(false)}
              saveCallback={(data) => {
                setShowComposer(false);
                updateReply(activityId, reply.id, processType, (reply) => {
                  setTranslatedContent({
                    language: undefined,
                    content: undefined,
                    quoted_content: undefined,
                  });

                  return {
                    ...reply,
                    ...omit(data, "author", "content"),
                    can: data.i.can,
                    published: data.created_at,
                    content: data.html_content,
                    original_content: data.content,
                    attachments: data.attachments,
                  };
                });
              }}
              isOpen
            />
          ) : (
            <>
              <ReplyHeader
                replyId={id}
                activityId={activityId}
                author={author}
                can={can}
                published={published}
                contentUpdatedAt={content_updated_at}
                contentUpdatedByName={content_updated_by?.name}
                quoteReply={quoteReply}
                setShowComposer={setShowComposer}
                translatedContent={translatedContent}
                setTranslatedContent={setTranslatedContent}
                type={processType}
              />
              {reply.quote_data && (
                <ReplyQuote
                  className="mt-2 mb-0 text-sm"
                  {...reply.quote_data}
                  content={
                    translatedContent.quoted_content || reply.quote_data.content
                  }
                  translated={!!translatedContent.language}
                />
              )}
              <div className="reply-content">
                <TruncateableContent
                  content={translatedContent.content || content}
                  truncateLength={250}
                  className={"break-words prose prose-sm"}
                />
                {translatedContent.language && <TranslationIndicator />}
              </div>
              <div className="flex flex-col gap-2 divide-y divide-dashed divide-neutral *:pt-3 text-sm">
                {!isEmpty(imageAttachments) && (
                  <ImageAttachments
                    images={imageAttachments as ImageAttachment[]}
                  />
                )}
                {!isEmpty(fileAttachments) && (
                  <FileAttachments
                    files={fileAttachments as FileAttachment[]}
                  />
                )}
              </div>
            </>
          )}
        </div>
        <div
          className={classNames({
            "flex justify-end": window.isApp && isReply,
          })}
        >
          <ReplyInteractions
            activityId={activityId}
            replyId={id}
            likeCount={reply.like_count}
            likes={reply.likes}
            myLike={reply.my_like}
            canLike={can.like}
            reactions={reply.reactions}
            myReaction={reply.my_reaction}
            canReact={can.react}
            type={processType}
            translatedContent={translatedContent}
            setTranslatedContent={setTranslatedContent}
            can={can}
            quoteReply={quoteReply}
            setShowComposer={setShowComposer}
          />
        </div>
      </div>
    </div>
  );
}
